<script setup lang="ts">
import { BarMitzvahType, TorahColumnOverlayType } from 'types/types'
import { computed, onMounted, reactive, watch, watchEffect } from 'vue'

import TorahColumnManageDialog from '@/components/TorahColumnManageDialog.vue'
import { queryTorahColumnOverlays } from '@/store/queryHelpers'

interface Props {
  barMitzvah: BarMitzvahType | null
  clearable?: boolean
  columnOverlay?: TorahColumnOverlayType | null
  disabled?: boolean
}

const xprops = withDefaults(defineProps<Props>(), {
  clearable: true,
  columnOverlay: null,
  disabled: false,
})

const emit = defineEmits<{
  requestUpload: []
  'update:columnOverlay': [overlay: TorahColumnOverlayType | null]
}>()

type SentinelKind = 'manage' | 'upload'

interface Sentinel {
  __sentinel__: SentinelKind
  id: string
  name: string
}

type SelectItem = Sentinel | TorahColumnOverlayType

const UPLOAD_SENTINEL: Sentinel = { __sentinel__: 'upload', id: '__upload__', name: 'Upload new column photo...' }
const MANAGE_SENTINEL: Sentinel = { __sentinel__: 'manage', id: '__manage__', name: 'Manage columns...' }

const state = reactive({
  managementVisible: false,
  overlays: [] as TorahColumnOverlayType[],
  selected: xprops.columnOverlay as TorahColumnOverlayType | null,
})

const items = computed((): SelectItem[] => {
  // Upload/Manage stay pinned at the top so they never scroll out of reach when a
  // student has many saved columns.
  const actions: SelectItem[] = [UPLOAD_SENTINEL]
  if (state.overlays.length > 0) {
    actions.push(MANAGE_SENTINEL)
  }
  return [...actions, ...state.overlays]
})

function isSentinel(item: SelectItem): item is Sentinel {
  return '__sentinel__' in item
}

function isUploadSentinel(item: SelectItem): boolean {
  return isSentinel(item) && item.__sentinel__ === 'upload'
}

function isManageSentinel(item: SelectItem): boolean {
  return isSentinel(item) && item.__sentinel__ === 'manage'
}

function itemTitle(item: SelectItem): string {
  return item.name
}

async function fetchOverlays() {
  if (!xprops.barMitzvah) {
    state.overlays = []
    return
  }
  try {
    state.overlays = await queryTorahColumnOverlays(xprops.barMitzvah.student.username)
  } catch (e) {
    console.log('ColumnSelect: failed to fetch overlays:', e)
    state.overlays = []
  }
}

function handleSelection(item: SelectItem | null) {
  if (item && isUploadSentinel(item)) {
    state.selected = null
    emit('requestUpload')
    return
  }
  if (item && isManageSentinel(item)) {
    state.selected = null
    state.managementVisible = true
    return
  }
  const overlay = item as TorahColumnOverlayType | null
  console.log(
    'ColumnSelect handleSelection:',
    overlay ? `id=${overlay.id} name=${overlay.name} skip=${overlay.skipPrefixWords} cut=${overlay.cutTrailingWords}` : 'null',
  )
  state.selected = overlay
}

async function onManageChanged() {
  await fetchOverlays()
  if (state.selected) {
    const stillExists = state.overlays.some((o) => o.id === state.selected!.id)
    if (!stillExists) {
      state.selected = null
    }
  }
}

/** Expose refetch so parent can call after upload completes. */
defineExpose({ fetchOverlays })

onMounted(() => {
  fetchOverlays()

  watch(
    () => xprops.barMitzvah,
    () => {
      state.selected = null
      fetchOverlays()
    },
  )

  watch(
    () => xprops.columnOverlay,
    (newVal) => {
      state.selected = newVal
    },
  )

  watchEffect(() => {
    emit('update:columnOverlay', state.selected)
  })
})
</script>

<template>
  <v-select
    :clearable="xprops.clearable"
    :disabled="xprops.disabled"
    :item-title="itemTitle"
    :items="items"
    label="Torah Column"
    :model-value="state.selected"
    return-object
    variant="underlined"
    @update:model-value="handleSelection"
  >
    <template #item="{ item, props: itemProps }">
      <v-list-item
        v-if="isUploadSentinel(item.raw)"
        v-bind="itemProps"
        prepend-icon="mdi-upload"
        :subtitle="undefined"
        title="Upload new column photo..."
      />
      <v-list-item
        v-else-if="isManageSentinel(item.raw)"
        v-bind="itemProps"
        prepend-icon="mdi-pencil-box-multiple"
        :subtitle="undefined"
        title="Manage columns..."
      />
      <v-list-item v-else v-bind="itemProps" :title="itemTitle(item.raw)" />
    </template>

    <template #selection="{ item }">
      <v-list-item :title="itemTitle(item.raw)" />
    </template>
  </v-select>

  <torah-column-manage-dialog
    :bar-mitzvah="xprops.barMitzvah"
    :overlays="state.overlays"
    :visible="state.managementVisible"
    @changed="onManageChanged"
    @close="state.managementVisible = false"
  />
</template>
